/**
 * Superadmin Feature Flag Management Hooks
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useToast } from "@wondermaps/ui";
import type { OrgTier } from "../contexts/OrgContext";

// Types
export type FeatureFlag = {
  id: string;
  key: string;
  name: string;
  description: string | null;
  flagType: string;
  enabled: boolean;
  percentage: number | null;
  variants: string[] | null;
  requiredTier: string | null;
  allowOrgOverride: boolean;
  category: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type OrgOverride = {
  id: string;
  flagId: string;
  orgId: string;
  enabled: boolean | null;
  percentage: number | null;
  variant: string | null;
  reason: string | null;
  expiresAt: string | null;
  createdAt: string;
  organization: { id: string; name: string };
  creator: { id: string; name: string | null; email: string } | null;
};

export type RoleOverride = {
  id: string;
  flagId: string;
  orgId: string;
  role: "owner" | "editor" | "viewer";
  enabled: boolean;
  reason: string | null;
  expiresAt: string | null;
  createdAt: string;
  organization: { id: string; name: string };
  creator: { id: string; name: string | null; email: string } | null;
};

export type OrgListItem = {
  id: string;
  name: string;
  slug: string;
  type: string;
  tier: string;
  memberCount: number;
  createdAt: string;
};

export type FlagFormData = {
  key: string;
  name: string;
  description: string;
  flagType: "boolean" | "percentage" | "variant";
  enabled: boolean;
  percentage: number;
  requiredTier: string;
  allowOrgOverride: boolean;
  category: "billing" | "admin_ui" | "visitor_ui" | "api";
};


// Queries
export function useSuperadminFlags() {
  return useQuery({
    queryKey: ["superadmin", "feature-flags"],
    queryFn: async () => {
      const res = await api["feature-flags"].$get({ query: {} });
      if (!res.ok) throw new Error("Failed to fetch feature flags");
      return res.json() as Promise<{ flags: FeatureFlag[] }>;
    },
  });
}

export function useFlagOverrideCounts(flags: FeatureFlag[] | undefined) {
  return useQuery({
    queryKey: ["superadmin", "feature-flags", "override-counts"],
    queryFn: async () => {
      const counts: Record<string, number> = {};
      await Promise.all(
        (flags ?? []).map(async (flag) => {
          const res = await api["feature-flags"][":id"].overrides.$get({ param: { id: flag.id } });
          counts[flag.id] = res.ok ? ((await res.json()).overrides as unknown[]).length : 0;
        })
      );
      return counts;
    },
    enabled: !!flags?.length,
  });
}

export function useFlagOverrides(flagId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["superadmin", "feature-flags", flagId, "overrides"],
    queryFn: async () => {
      if (!flagId) return { overrides: [] };
      const res = await api["feature-flags"][":id"].overrides.$get({ param: { id: flagId } });
      if (!res.ok) throw new Error("Failed to fetch overrides");
      return res.json() as Promise<{ overrides: OrgOverride[] }>;
    },
    enabled: !!flagId && enabled,
  });
}

export function useFlagRoleOverrides(flagId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["superadmin", "feature-flags", flagId, "role-overrides"],
    queryFn: async () => {
      if (!flagId) return { roleOverrides: [] };
      const res = await api["feature-flags"][":id"]["role-overrides"].$get({ param: { id: flagId } });
      if (!res.ok) throw new Error("Failed to fetch role overrides");
      return res.json() as Promise<{ roleOverrides: RoleOverride[] }>;
    },
    enabled: !!flagId && enabled,
  });
}

export function useFlagDetail(flagId: string | undefined) {
  const flagsQuery = useSuperadminFlags();
  const overridesQuery = useFlagOverrides(flagId, !!flagId);
  const roleOverridesQuery = useFlagRoleOverrides(flagId, !!flagId);
  const orgsQuery = useSuperadminOrgs(!!flagId);

  const flag = flagsQuery.data?.flags.find((f) => f.id === flagId);

  return {
    flag,
    isLoading: flagsQuery.isLoading,
    overrides: overridesQuery.data?.overrides,
    overridesLoading: overridesQuery.isLoading,
    roleOverrides: roleOverridesQuery.data?.roleOverrides,
    roleOverridesLoading: roleOverridesQuery.isLoading,
    orgs: orgsQuery.data?.orgs,
  };
}

export function useSuperadminOrgs(enabled: boolean) {
  return useQuery({
    queryKey: ["superadmin", "orgs"],
    queryFn: async () => {
      const res = await api.orgs.admin.$get();
      if (!res.ok) throw new Error("Failed to fetch orgs");
      return res.json() as Promise<{ orgs: OrgListItem[] }>;
    },
    enabled,
  });
}

// Mutations
function useFlagMutation<T, R>(
  mutationFn: (args: T) => Promise<R>,
  invalidateKeys: string[][],
  successMsg: string,
  onSuccess?: () => void
) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      invalidateKeys.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
      toast({ title: successMsg });
      onSuccess?.();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });
}

export function useToggleFlagEnabled() {
  return useFlagMutation(
    async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const res = await api["feature-flags"][":id"].$patch({ param: { id }, json: { enabled } });
      if (!res.ok) throw new Error("Failed to update flag");
      return res.json();
    },
    [["superadmin", "feature-flags"]],
    "Flag updated"
  );
}

export function useDeleteFlag() {
  return useFlagMutation(
    async (id: string) => {
      const res = await api["feature-flags"][":id"].$delete({ param: { id } });
      if (!res.ok) throw new Error("Failed to delete flag");
      return res.json();
    },
    [["superadmin", "feature-flags"]],
    "Flag deleted"
  );
}

export function useCreateFlag(onSuccess: () => void) {
  return useFlagMutation(
    async (data: FlagFormData) => {
      const res = await api["feature-flags"].$post({
        json: {
          key: data.key,
          name: data.name,
          description: data.description || undefined,
          flagType: data.flagType,
          enabled: data.enabled,
          percentage: data.flagType === "percentage" ? data.percentage : undefined,
          requiredTier: (data.requiredTier === "none" ? undefined : data.requiredTier) as OrgTier | undefined,
          allowOrgOverride: data.allowOrgOverride,
          category: data.category,
        },
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error((error as { error?: string }).error || "Failed to create flag");
      }
      return res.json();
    },
    [["superadmin", "feature-flags"]],
    "Flag created",
    onSuccess
  );
}

export function useUpdateFlag(onSuccess: () => void) {
  return useFlagMutation(
    async ({ id, data }: { id: string; data: FlagFormData }) => {
      const res = await api["feature-flags"][":id"].$patch({
        param: { id },
        json: {
          name: data.name,
          description: data.description || undefined,
          enabled: data.enabled,
          percentage: data.flagType === "percentage" ? data.percentage : undefined,
          requiredTier: (data.requiredTier === "none" ? undefined : data.requiredTier) as OrgTier | undefined,
          allowOrgOverride: data.allowOrgOverride,
          category: data.category,
        },
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error((error as { error?: string }).error || "Failed to update flag");
      }
      return res.json();
    },
    [["superadmin", "feature-flags"]],
    "Flag updated",
    onSuccess
  );
}

